import React, { useEffect, useRef, useState } from 'react';
import { useFormatTarget, formatActions } from '../lib/formatTarget';
import { MOD_KEY, IS_MAC } from '../utils/platform';

// Platform-aware shortcut labels for the button tooltips. Same convention as
// ShortcutsModal (Mac shows ⌘/⌥/⇧ without "+"; everyone else Ctrl/Alt/Shift+).
// Walter 2026-07-02: "indicate the keyboard shortcuts in the tooltip."
const SEP = IS_MAC ? '' : '+';
const K_HL = `${MOD_KEY}${SEP}h`;
const K_DONE = IS_MAC ? `⇧${MOD_KEY}h` : `Shift+${MOD_KEY}+h`;
const K_BOLD = `${MOD_KEY}${SEP}b`;
const K_ITAL = `${MOD_KEY}${SEP}i`;
const K_ALT = IS_MAC ? '⌥' : 'Alt';
const K_SHIFT = IS_MAC ? '⇧' : 'Shift';
const K_MOVE_UP = IS_MAC ? `${K_ALT}${K_SHIFT}↑` : `${K_ALT}+${K_SHIFT}+↑`;
const K_MOVE_DOWN = IS_MAC ? `${K_ALT}${K_SHIFT}↓` : `${K_ALT}+${K_SHIFT}+↓`;
// Alt+Shift+S — Walter's pick (2026-07-16 rd 3; no cross-app standard).
const K_STRIKE = IS_MAC ? `${K_ALT}${K_SHIFT}s` : `${K_ALT}+${K_SHIFT}+s`;
// Ctrl+Alt+n — deliberately Ctrl (not ⌘) on Mac too: the handler binds
// ctrlKey+altKey only (⌘-number switches browser tabs). Walter 2026-07-16.
const kHeader = (n) => (IS_MAC ? `⌃${K_ALT}${n}` : `Ctrl+Alt+${n}`);

/**
 * FormatMenu — Walter 2026-06-24; icon refresh Walter 2026-07-16 (approved
 * mockup). CM6 hides raw markup, so buttons show monochrome ICONS instead of
 * raw `#`/`^^`/`[x]` chars; the syntax + shortcut moved into the TOOLTIPS
 * (still learnable). Added for the CM6 era: bold, italic, empty checkbox.
 *
 * Responsive: WIDE → an inline button strip framed as a labelled "format"
 * block; NARROW → a compact `Format ▾` dropdown. Greyed unless a list editor
 * (textarea OR CM6 live-preview) is focused. Buttons show as ACTIVE when the
 * caret's line already has that format and TOGGLE it off on a second click
 * (live caret tracking lives in formatTarget). Every button `preventDefault`s
 * its mousedown so a click never blurs the editor — the action lands on the
 * line you were editing.
 */

export const INLINE_MIN_WIDTH = 1200; // inline strip at/above this width; dropdown below

const HL = [
  { idx: 1, label: 'green', varName: '--hl-green' },
  { idx: 2, label: 'blue', varName: '--hl-blue' },
  { idx: 3, label: 'red', varName: '--hl-red' },
];

const keepFocus = (e) => e.preventDefault();
const fire = (fn) => (e) => { e.preventDefault(); fn(); };

function useViewportWide(min) {
  const get = () => (typeof window !== 'undefined' ? window.innerWidth >= min : true);
  const [wide, setWide] = useState(get);
  useEffect(() => {
    const on = () => setWide(get());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return wide;
}

// ── monochrome icon set (Walter 2026-07-16 mockup) ──────────────────────────
// All inherit currentColor so they turn accent when active. Shared with
// MobileFormatBar via the exports below.
export function BellIcon({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 2a3.2 3.2 0 0 0-3.2 3.2c0 2.4-.9 3.6-1.5 4.2-.3.3-.1.8.3.8h8.8c.4 0 .6-.5.3-.8-.6-.6-1.5-1.8-1.5-4.2A3.2 3.2 0 0 0 8 2z"
        stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M6.6 12.4a1.5 1.5 0 0 0 2.8 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
export function CheckboxEmptyIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2.6" y="2.6" width="10.8" height="10.8" rx="2.2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
export function CheckboxDoneIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2.6" y="2.6" width="10.8" height="10.8" rx="2.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.2 8.2l1.9 2 3.6-3.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function ArrowIcon({ dir, size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true"
      style={dir === 'down' ? { transform: 'scaleY(-1)' } : undefined}>
      <path d="M8 12.5V4M4.5 7.3L8 3.6l3.5 3.7" stroke="currentColor" strokeWidth="1.3"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
// Typographic glyphs — B (extra-bold), I (serif italic), H with level subscript.
export function BoldGlyph() {
  return <span style={{ fontWeight: 800, fontSize: '1.15em' }}>B</span>;
}
export function ItalicGlyph() {
  return <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic', fontWeight: 600, fontSize: '1.25em' }}>I</span>;
}
export function StrikeGlyph() {
  return <span style={{ textDecoration: 'line-through', fontWeight: 600, fontSize: '1.1em' }}>S</span>;
}
export function HeaderGlyph({ level }) {
  return (
    <span style={{ letterSpacing: 0.3 }}>
      <span style={{ fontWeight: 700 }}>H</span>
      <sub style={{ fontSize: '0.7em', fontWeight: 600 }}>{level}</sub>
    </span>
  );
}
export function LogGlyph() {
  return <span style={{ fontWeight: 700 }}>@</span>;
}

// Tooltip copy: the syntax chars PREPEND the name ("^green highlight",
// "**bold**") instead of a separate "— syntax" segment — Walter 2026-07-19:
// "don't say '^text', just prepend". Shortcut still rides after the ·.
export const TIPS = {
  bold: `**bold** · ${K_BOLD}`,
  italic: `*italic* · ${K_ITAL}`,
  strike: `~~strikethrough~~ · ${K_STRIKE}`,
  hl: (h) => `${'^'.repeat(h.idx)}${h.label} highlight · ${K_HL} cycles colours`,
  header: (n) => `${'#'.repeat(n)} heading ${n} · ${kHeader(n)}`,
  checkbox: '- [ ] checkbox',
  done: `[x] mark as done · ${K_DONE}`,
  reminder: 'insert a reminder on this line — type a date + what to do',
  log: 'log this line — stamps today\'s @date (shows in Logs)',
  moveUp: `move line up · ${K_MOVE_UP}`,
  moveDown: `move line down · ${K_MOVE_DOWN}`,
};

// ── shared compact button ───────────────────────────────────────────────────
function Cell({ title, onClick, active, swatchBg, children, style }) {
  return (
    <button
      type="button" title={title}
      onMouseDown={keepFocus} onClick={onClick}
      style={{
        // Walter 2026-06-25: compact — the strip was eating too much header
        // space.
        minWidth: 21, height: 19, padding: '0 4px',
        // Active = a thin accent outline + BOLD glyph (Walter 2026-06-24: the
        // earlier inset ring read too chunky on the colour swatches).
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border-bright)'}`,
        borderRadius: 4,
        background: swatchBg || (active ? 'var(--accent-wash)' : 'var(--surface2)'),
        color: active && !swatchBg ? 'var(--accent)' : 'var(--text)',
        fontWeight: active ? 700 : 400,
        fontFamily: 'var(--mono)', fontSize: 'calc(9.5px * var(--ui-scale, 1))',
        cursor: 'pointer', lineHeight: 1,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        ...style,
      }}
    >{children}</button>
  );
}

function EmphasisCells() {
  // Action buttons (wrap/unwrap the selection) — no line-level active state.
  return (
    <>
      <Cell title={TIPS.bold} onClick={fire(() => formatActions.bold())}><BoldGlyph /></Cell>
      <Cell title={TIPS.italic} onClick={fire(() => formatActions.italic())}><ItalicGlyph /></Cell>
      <Cell title={TIPS.strike} onClick={fire(() => formatActions.strike())}><StrikeGlyph /></Cell>
    </>
  );
}
function HighlightCells({ line }) {
  // Plain colour chips — the colour carries the meaning (mockup call C).
  return HL.map((h) => (
    <Cell key={h.idx} title={TIPS.hl(h)} active={line.color === h.idx}
      swatchBg={`var(${h.varName})`} onClick={fire(() => formatActions.highlight(h.idx))}
      style={{ minWidth: 23 }}>{' '}</Cell>
  ));
}
function HeaderCells({ line }) {
  return [1, 2, 3].map((lvl) => (
    <Cell key={lvl} title={TIPS.header(lvl)} active={line.headerLevel === lvl}
      onClick={fire(() => formatActions.header(lvl))}><HeaderGlyph level={lvl} /></Cell>
  ));
}
function CheckboxCell({ line }) {
  return (
    <Cell title={TIPS.checkbox} active={line.checkbox}
      onClick={fire(() => formatActions.checkbox())}><CheckboxEmptyIcon /></Cell>
  );
}
function DoneCell({ line }) {
  return (
    <Cell title={TIPS.done} active={line.done}
      onClick={fire(() => formatActions.done())}><CheckboxDoneIcon /></Cell>
  );
}
// Move the current line (or selected block) up / down. Action buttons — no
// "active" state to show. Walter 2026-07-02.
function MoveCells() {
  return (
    <>
      <Cell title={TIPS.moveUp} onClick={fire(() => formatActions.moveLine('up'))}><ArrowIcon dir="up" /></Cell>
      <Cell title={TIPS.moveDown} onClick={fire(() => formatActions.moveLine('down'))}><ArrowIcon dir="down" /></Cell>
    </>
  );
}
function ReminderCell({ line }) {
  return (
    <Cell title={TIPS.reminder} active={line.bell}
      onClick={fire(() => formatActions.reminder())}><BellIcon /></Cell>
  );
}
function LogCell({ line }) {
  // Log keeps the @ glyph — Walter's call (2026-07-16 mockup review).
  return (
    <Cell title={TIPS.log} active={line.log} onClick={fire(() => formatActions.log())}
      style={{ fontSize: 'calc(12px * var(--ui-scale, 1))' }}><LogGlyph /></Cell>
  );
}
function Divider() {
  return <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 3px' }} />;
}

// ── inline strip (wide) — framed as a labelled "format" block ────────────────
function InlineStrip({ disabled, line, hideReminder }) {
  return (
    <div
      title={disabled ? 'click into a list to format a line' : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        border: '1px solid var(--border)', borderRadius: 6,
        padding: '2px 6px',
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        transition: 'opacity 0.12s',
      }}
    >
      {/* Walter 2026-06-24: dropped the "format" legend — the bordered block
          alone reads as a grouped control; the label didn't look great.
          Grouping: MOVE · emphasis · highlights · headings · checkboxes ·
          special lines. Walter 2026-08-22 moved the arrows from last to
          FIRST — "I use the up down arrows more than other formatting
          buttons", and they were the furthest thing from the mouse. */}
      <MoveCells />
      <Divider />
      <EmphasisCells />
      <Divider />
      <HighlightCells line={line} />
      <Divider />
      <HeaderCells line={line} />
      <Divider />
      <CheckboxCell line={line} />
      <DoneCell line={line} />
      <Divider />
      {/* hideReminder: surfaces with no reminder-capture engine (the
          standalone /list page) — a menu-inserted 🔔 there would be a dead
          bell that never creates a reminder doc. Walter 2026-07-19. */}
      {!hideReminder && <ReminderCell line={line} />}
      <LogCell line={line} />
    </div>
  );
}

// ── dropdown (narrow) ───────────────────────────────────────────────────────
function Dropdown({ disabled, line, hideReminder }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open]);

  const run = (fn) => (e) => { e.preventDefault(); fn(); setOpen(false); };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button" disabled={disabled}
        title={disabled ? 'click into a list to format a line' : 'format the current line'}
        onMouseDown={disabled ? undefined : keepFocus}
        onClick={disabled ? undefined : () => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5, height: 28, padding: '0 9px',
          background: open ? 'var(--surface2)' : 'transparent',
          border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          color: disabled ? 'var(--text-dimmer)' : 'var(--text-dim)',
          fontFamily: 'var(--mono)', fontSize: 'calc(12px * var(--ui-scale, 1))',
          cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
        }}
      >
        format <span style={{ fontSize: '1.15em', lineHeight: 1 }}>▾</span>
      </button>

      {open && !disabled && (
        <div role="menu" style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, minWidth: 210,
          background: 'var(--surface)', border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-modal-mid)',
          padding: '8px', zIndex: 2400, fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))', color: 'var(--text)',
        }}>
          <Row onClick={run(() => formatActions.bold())}><BoldGlyph /> bold <Kbd>{K_BOLD}</Kbd></Row>
          <Row onClick={run(() => formatActions.italic())}><ItalicGlyph /> italic <Kbd>{K_ITAL}</Kbd></Row>
          <Row onClick={run(() => formatActions.strike())}><StrikeGlyph /> strikethrough <Kbd>{K_STRIKE}</Kbd></Row>
          <div style={{ height: 1, background: 'var(--border)', margin: '6px 0' }} />
          <Section label={`highlight · ${K_HL} cycles`}><div style={{ display: 'flex', gap: 6 }}><HighlightCells line={line} /></div></Section>
          <Section label={`header · ${kHeader('1/2/3')}`}><div style={{ display: 'flex', gap: 6 }}><HeaderCells line={line} /></div></Section>
          <div style={{ height: 1, background: 'var(--border)', margin: '6px 0' }} />
          <Row active={line.checkbox} onClick={run(() => formatActions.checkbox())}><CheckboxEmptyIcon size={12} /> checkbox</Row>
          <Row active={line.done} onClick={run(() => formatActions.done())}><CheckboxDoneIcon size={12} /> mark as done <Kbd>{K_DONE}</Kbd></Row>
          {!hideReminder && <Row active={line.bell} onClick={run(() => formatActions.reminder())}><BellIcon /> reminder</Row>}
          <Row active={line.log} onClick={run(() => formatActions.log())}><LogGlyph /> log (date-stamp)</Row>
          <div style={{ height: 1, background: 'var(--border)', margin: '6px 0' }} />
          <Row onClick={run(() => formatActions.moveLine('up'))}><ArrowIcon dir="up" size={11} /> move line up <Kbd>{K_MOVE_UP}</Kbd></Row>
          <Row onClick={run(() => formatActions.moveLine('down'))}><ArrowIcon dir="down" size={11} /> move line down <Kbd>{K_MOVE_DOWN}</Kbd></Row>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ color: 'var(--text-dimmer)', fontSize: '0.85em', marginBottom: 4, letterSpacing: 0.3 }}>{label}</div>
      {children}
    </div>
  );
}
// Small right-aligned shortcut hint inside a dropdown Row.
function Kbd({ children }) {
  return (
    <span style={{
      marginLeft: 'auto', paddingLeft: 8,
      color: 'var(--text-dimmer)', fontSize: '0.85em', whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}
function Row({ children, onClick, active }) {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" onMouseDown={keepFocus} onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
        padding: '6px', border: 'none', borderRadius: 6,
        background: active ? 'var(--accent-wash)' : (hover ? 'var(--surface2)' : 'transparent'),
        color: active ? 'var(--accent)' : 'var(--text)',
        fontFamily: 'var(--mono)', fontSize: 'inherit', cursor: 'pointer',
      }}
    >{children}</button>
  );
}

export default function FormatMenu({ hideReminder = false }) {
  const { focused, line } = useFormatTarget();
  const wide = useViewportWide(INLINE_MIN_WIDTH);
  return wide
    ? <InlineStrip disabled={!focused} line={line} hideReminder={hideReminder} />
    : <Dropdown disabled={!focused} line={line} hideReminder={hideReminder} />;
}
